import { useEffect, useState } from "react";
import { Checkbox, FormControlLabel, FormGroup, Grid } from "@mui/material";
import { t } from "i18next";

//import custom hook
import useUserInfo from "../../../hooks/useUserInfo";

//import constants
import { IS_MOBILE } from "../../../constants/breakpoints";

//import utils
import { numberWithCommas } from "../../../utils/utility";

//import general components
import { BaseButton } from "../../common/base-button/base-button";
import BaseTextField from "../../common/base-text-field/base-text-field";

//import images and icons
import emptyImage from "../../../assets/imgs/empty pic.jpg";
import { RefreshCircle, Stop, TickSquare } from "iconsax-react";

function PayButton({ booking, isZm, captchaValue, loading }) {
    return (
        <Grid container item xs={4} md={5.5} lg={2} justifyContent="flex-end">
            <BaseButton
                text={t("confirmAndPay")}
                color="primary"
                size="medium"
                radius="radius-10"
                handleClick={booking}
                loading={loading}
                disabled={isZm && !captchaValue}
            />
        </Grid>
    );
}

function PayInformation({
    loading,
    totalPrice,
    newTotalPrice,
    captcha,
    captchaValue,
    setCaptchaValue,
    refreshCaptcha,
    booking,
    editInformation,
    isPaymentByCredit,
    setIsPaymentByCredit,
}) {
    //request to get user information
    const { userInformation, getUserInfo } = useUserInfo();

    useEffect(() => {
        getUserInfo();
    }, []);

    //sessionStorage variables
    const selectedFlight = JSON.parse(sessionStorage.getItem("selectedFlight"));
    const isZm = selectedFlight?.tag === "zm" || selectedFlight?.tag === "zm";

    //handle captcha change
    const handleCaptchaChange = event => {
        const { value } = event.target;
        setCaptchaValue(value);
    };

    //handle changes in credit payment option and update payment details
    const handleCreditPaymentOptionChange = event => {
        const { checked } = event.target;
        updatePaymentDetails(checked);
        setIsPaymentByCredit(checked);
    };

    // State to track the amount to be paid using credit
    const [remainingPaymentAmount, setRemainingPaymentAmount] = useState(0);
    //calculate the amount to pay based on user's credit balance and total price
    const updatePaymentDetails = isUsingCredit => {
        const userBalance = Math.max(0, userInformation?.balance) || 0;
        const price = newTotalPrice || totalPrice;
        const amountToPay = isUsingCredit ? Math.max(0, price - userBalance) : totalPrice;

        setRemainingPaymentAmount(amountToPay);
    };

    return (
        <Grid className="confirm-information__pay-information" container item xs={12} rowGap={2}>
            <Grid container item xs={12}>
                <FormGroup className="base-filter__filter-label">
                    <FormControlLabel
                        control={
                            <Checkbox
                                onChange={event => handleCreditPaymentOptionChange(event)}
                                name="creditPayment"
                                icon={
                                    <Stop
                                        size={IS_MOBILE ? "22" : "24"}
                                        color={userInformation?.balance === 0 ? "var(--medium-gray-color)" : "var(--teal-color-18)"}
                                    />
                                }
                                checkedIcon={<TickSquare size={IS_MOBILE ? "22" : "24"} color="var(--secondary-color)" variant="Bold" />}
                                disabled={userInformation?.balance === 0}
                            />
                        }
                        label={`${t("General.Credit Payment")} (${t("General.Your Credit")}: ${numberWithCommas(userInformation?.balance) || 0} ${t(
                            "currency",
                        )}) `}
                        sx={{ margin: 0, padding: "4px" }}
                    />
                </FormGroup>
            </Grid>
            <Grid className="confirm-information__total-price" container item xs={12} alignItems="center" order={{ xs: 2, md: 1 }}>
                <Grid container item xs={8} md={12} columnGap={0.5} justifyContent={{ xs: "flex-start", md: "space-between" }}>
                    <span>{t("amountPayable")}: </span>
                    <span>
                        {isPaymentByCredit ? numberWithCommas(remainingPaymentAmount) : numberWithCommas(newTotalPrice || totalPrice)} {t("currency")}
                    </span>
                </Grid>
                {IS_MOBILE && <PayButton booking={booking} isZm={isZm} captchaValue={captchaValue} loading={loading} />}
            </Grid>

            <Grid
                className="confirm-information__submit-information"
                container
                item
                xs={12}
                justifyContent="flex-end"
                alignItems="center"
                order={{ xs: 1, md: 2 }}
            >
                {isZm && (
                    <Grid container item md={6} justifyContent="flex-end" rowGap={1}>
                        <Grid container item xs={12} md={6}>
                            {t("enterNumber")}
                        </Grid>
                        <Grid container item md={6} rowGap={2} alignItems="center">
                            <Grid className="confirm-information__captcha" container item xs={6} md={12} alignItems="center" order={{ xs: 2, md: 1 }}>
                                <RefreshCircle size="32" color="var(--primary-color)" variant="Bold" onClick={refreshCaptcha} />
                                <img className="confirm-information__captcha-image" src={captcha?.return?.captcha || emptyImage} />
                                <img className="confirm-information__captcha-image" src={captcha?.departure?.captcha || emptyImage} />
                            </Grid>
                            <Grid container item xs={6} md={10} order={{ xs: 1, md: 2 }}>
                                <BaseTextField
                                    label={t("imageNumbers")}
                                    dir="ltr"
                                    type="number"
                                    value={captchaValue}
                                    setValue={handleCaptchaChange}
                                />
                                <span className="confirm-information__error-message">{isZm && !captchaValue ? "* کپچا اجباریست." : ""}</span>
                            </Grid>
                        </Grid>
                    </Grid>
                )}
                {!IS_MOBILE && (
                    <Grid container item xs={12} md={6} columnGap={1} justifyContent="flex-end">
                        <Grid container item xs={12} md={5.5} lg={2} justifyContent="flex-end">
                            <BaseButton text={t("edit")} color="outline" size="medium" radius="radius-10" handleClick={editInformation} />
                        </Grid>
                        <PayButton booking={booking} isZm={isZm} captchaValue={captchaValue} loading={loading} />
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
}

export default PayInformation;
